import { getMembershipFee, getMembershipGST, getMembershipTotal } from "../components/web/VoucherCheckoutPanel";

/**
 * One "order" describes everything the customer is paying for at a store.
 * Any combination of the three parts works (at least one is required):
 *
 * {
 *   store:      { id, name, address, cityName, logo },
 *   membership: { membershipId, name, owned, fee, action,maxCoinPercent }   // action: "use" | "join" | "buy"
 *   paybill:    { amount, discount, cashback, rewardId, rewardName },
 *   vouchers:   [{ voucher_id, name, qty, face, discount, payable, gtcoin }]   // face/discount/payable/gtcoin are per unit
 *   gift:       { name, mobile } | null   // vouchers bought for someone else
 *   successPath: "/customer/..."   // optional, where to go after payment
 * }
 */

const KEY = "storeCheckoutOrder";
const num = (v) => Number(v) || 0;
const round2 = (n) => Math.round(n * 100) / 100;

/* ── persistence (so a refresh on the payment page keeps working) ── */

export const saveCheckoutOrder = (order) => {
    try {
        sessionStorage.setItem(KEY, JSON.stringify(order));
    } catch {
        /* storage unavailable: router state still carries the order */
    }
};

export const loadCheckoutOrder = (stateOrder) => {
    if (stateOrder) return stateOrder;
    try {
        const raw = sessionStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const clearCheckoutOrder = () => {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
};

/* ── validation ── */

export const isValidOrder = (order) => {
    const hasContent = Boolean(
        order?.paybill || order?.vouchers?.length || (order?.membership && !order.membership.owned)
    );
    if (!hasContent) return false;
    // Pay-bill / voucher purchases need a store. A standalone membership
    // purchase/renewal/upgrade (e.g. started from the home screen, with no
    // store context) doesn't - it's the only content on the order.
    if (order?.store?.id != null) return true;
    return Boolean(order?.membership) && !order?.paybill && !order?.vouchers?.length;
};

/* ── summary: every amount the page shows comes from here ── */

export function buildOrderSummary(order) {
    const m = order?.membership || null;
    const membershipFee = getMembershipFee(m);
    const membershipGST = getMembershipGST(m);
    const membershipTotal = getMembershipTotal(m);

    const pb = order?.paybill || null;
    const bill = pb
        ? {
              amount: round2(num(pb.amount)),
              discount: round2(num(pb.discount)),
              payable: round2(num(pb.amount) - num(pb.discount)),
              cashback: round2(num(pb.cashback)),
              rewardId: pb.rewardId ?? null,
              rewardName: pb.rewardName || "",
          }
        : null;

    const items = (order?.vouchers || []).map((v) => ({
        ...v,
        qty: num(v.qty),
        lineFace: round2(num(v.face) * num(v.qty)),
        lineDiscount: round2(num(v.discount) * num(v.qty)),
        linePayable: round2(num(v.payable) * num(v.qty)),
        lineCoins: num(v.gtcoin) * num(v.qty), // GoerCoin needed for this line (gtcoin is per unit)
    }));
    const vo = order?.voucherOrder || null; // server order from /voucher/voucher-checkout

    const vouchers = items.length
        ? {
            items,
            count: items.reduce((s, i) => s + i.qty, 0),
            face: vo
            ? round2(
                Array.isArray(vo.items) && vo.items.length
                    ? vo.items.reduce((s, i) => s + num(i.line_total), 0)
                    : num(vo.subtotal) + num(vo.discount)
            )
            : round2(items.reduce((s, i) => s + i.lineFace, 0)),
            discount: vo ? round2(num(vo.discount)) : round2(items.reduce((s, i) => s + i.lineDiscount, 0)),
            tax: vo ? round2(num(vo.tax_amount)) : 0,
            payable: vo ? round2(num(vo.total_amount)) : round2(items.reduce((s, i) => s + i.linePayable, 0)),
            coins: items.reduce((s, i) => s + i.lineCoins, 0),
            orderNo: vo?.order_no ?? null,
            expiresAt: vo?.expires_at ?? null,
        }
        : null;

    const total = round2((bill?.payable || 0) + (vouchers?.payable || 0) + membershipTotal);
    const savings = round2((bill?.discount || 0) + (vouchers?.discount || 0));

    // "Pay bill + 2 vouchers + VIP Pro Member membership"
    const parts = [];
    if (bill) parts.push("Pay bill");
    if (vouchers) parts.push(`${vouchers.count} voucher${vouchers.count === 1 ? "" : "s"}`);
    if (m) parts.push(`${m.name} membership`);

    return {
        bill,
        vouchers,
        membership: m ? { ...m, fee: membershipFee, gst: membershipGST, total: membershipTotal } : null,
        total,
        savings,
        gift: vouchers && order?.gift ? order.gift : null,
        cashback: bill?.cashback || 0,
        title: parts.join(" + "),
    };
}

/**
 * Body for your purchase API / the encrypted gateway blob.
 * `pay` = { usewallet, usereward, walletApplied, rewardApplied, gatewayAmount }
 */
export function buildOrderPayload(order, summary, customerId, pay) {
    const m = summary.membership;
    return {
        store_id: order.store?.id ?? null,
        customer_id: customerId,
        total: summary.total,
        membership: m
            ? { program_id: m.membershipId, action: m.action || (m.owned ? "use" : "buy"), fee: m.fee, gst: m.gst }
            : null,
        paybill: summary.bill
            ? {
                  amount: summary.bill.amount,
                  discount: summary.bill.discount,
                  payable: summary.bill.payable,
                  cashback: summary.bill.cashback,
                  reward_id: summary.bill.rewardId,
              }
            : null,
        vouchers: summary.vouchers
            ? summary.vouchers.items.map((i) => ({ voucher_id: i.voucher_id, quantity: i.qty, gtcoin_required: i.lineCoins }))
            : null,
        is_gift: summary.gift ? 1 : 0,
        gift_name: summary.gift?.name ?? null,
        gift_mobile: summary.gift?.mobile ?? null,
        usewallet: pay.usewallet,
        usereward: pay.usereward,
        wallet_amount: pay.walletApplied,
        reward_amount: pay.rewardApplied,
        online_amount: pay.gatewayAmount,
    };
}

/** Max % of an order payable with GoerCoin for this membership (0-100). Missing value = 100 (no limit). */
export const getMaxCoinPercent = (membership) => {
    const p = membership?.maxCoinPercent;
    if (p === null || p === undefined || p === "") return 100;
    return Math.min(100, Math.max(0, Number(p) || 0));
};

/** { percent, cap, usable }: cap = ₹ limit for this order, usable = min(coin balance, cap) */
export function getCoinLimit(membership, orderTotal, coinBalance) {
    const percent = getMaxCoinPercent(membership);
    const cap = round2((num(orderTotal) * percent) / 100);
    return { percent, cap, usable: round2(Math.min(num(coinBalance), cap)) };
}